import { Box, Typography, Grid, Container } from '@mui/material';
import { Target, Award, Shield, Users, CheckCircle2 } from 'lucide-react';
import { useEffect } from 'react';
import "aos/dist/aos.css";
import AOS from "aos";

function OurMission() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      offset: 100,
      easing: 'ease-out-cubic',
    });
  }, []);

  const missionPoints = [
    {
      icon: Award,
      title: "Premium Craftsmanship",
      description: "Every detail executed to perfection"
    },
    {
      icon: Users,
      title: "Transparent Communication",
      description: "Clear, honest dialogue at every stage"
    },
    {
      icon: CheckCircle2,
      title: "Fixed-Price Clarity",
      description: "No hidden costs, no surprises"
    },
    {
      icon: Shield,
      title: "Strong Site Supervision",
      description: "Rigorous oversight on every project"
    },
    {
      icon: Target,
      title: "Ethical Building Practices",
      description: "Integrity in every decision we make"
    }
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        mt: { xs: 5, md: 12 },
        py: { xs: 6, md: 10 },
        background: 'linear-gradient(135deg, var(--secondary-color) 0%, #ffffff 100%)',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 5, md: 8 }}>
          <Typography
            data-aos="fade-down"
            className="fw-bold text-second"
            sx={{
              fontSize: { xs: '32px', md: '48px' },
              mb: 2,
              letterSpacing: 1
            }}
          >
            OUR MISSION
          </Typography>
          
          <Box
            data-aos="fade-up"
            sx={{
              width: { xs: '60px', md: '80px' },
              height: '4px',
              backgroundColor: 'var(--primary-text-color)',
              margin: '0 auto',
              mb: 4
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '16px', md: '20px' },
              color: 'var(--black-color)',
              textAlign: 'center',
              fontWeight: 500,
              lineHeight: 1.8,
              fontStyle: 'italic'
            }}
          >
            To build every home to a standard we would expect for our own family.
          </Typography>
        </Box>
            
        {/* Mission Statement */}
        {/* <Box
          data-aos="zoom-in"
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            mb: { xs: 4, md: 6 },
            boxShadow: '0 10px 40px rgba(19,25,83,0.08)',
            border: '2px solid var(--primary-text-color)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '6px',
              background: 'linear-gradient(90deg, var(--primary-color) 0%, var(--primary-text-color) 100%)'
            }
          }}
        >
          <Typography
            className="text-second fw-bold"
            sx={{
              fontSize: { xs: '20px', md: '28px' },
              mb: 3,
              textAlign: 'center'
            }}
          >
            Our mission is simple:
          </Typography>
          
          <Typography
            sx={{
              fontSize: { xs: '16px', md: '20px' },
              color: 'var(--black-color)',
              textAlign: 'center',
              fontWeight: 500,
              lineHeight: 1.8,
              fontStyle: 'italic'
            }}
          >
            To build every home to a standard we would expect for our own family.
          </Typography>
        </Box> */}

        {/* What We Combine Section */}
        <Box mb={{ xs: 4, md: 6 }}>
          <Typography
            data-aos="fade-right"
            className="text-second fw-bold"
            sx={{
              fontSize: { xs: '24px', md: '32px' },
              mb: 4,
              textAlign: 'center'
            }}
          >
            We aim to combine:
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {missionPoints.map((point, index) => {
              const IconComponent = point.icon;
              return (
                <Grid item xs={12} sm={6} md={'grow'} key={index} sx={{ maxWidth: { md: '350px' }, minWidth: { md: '300px' } }}>
                  <Box
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    sx={{
                      backgroundColor: '#ffffff',
                      p: 3,
                      borderRadius: 2,
                      height: '100%',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      border: '1px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                        borderColor: 'var(--primary-text-color)',
                        '& .mission-icon': {
                          backgroundColor: 'var(--primary-color)'
                        },
                        '& .mission-icon svg': {
                          color: 'var(--primary-text-color)'
                        }
                      }
                    }}
                  >
                    <Box
                      className="mission-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: 'var(--secondary-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        transition: 'all 0.3s ease',
                        '& svg': {
                          color: 'var(--primary-color)',
                          transition: 'color 0.3s ease'
                        }
                      }}
                    >
                      <IconComponent size={28} />
                    </Box>
                    
                    <Typography
                      className="fw-bold text-second"
                      sx={{ fontSize: '18px', mb: 1 }}
                    >
                      {point.title}
                    </Typography>
                    
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'var(--black-light-color)',
                        lineHeight: 1.6
                      }}
                    >
                      {point.description}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Belief Statement */}
        <Box
          data-aos="fade-up"
          sx={{
            backgroundColor: 'var(--primary-color)',
            borderRadius: 3,
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-10%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              backgroundColor: 'rgba(192,174,93,0.1)',
              zIndex: 0
            }
          }}
        >
          <Box position="relative" zIndex={1}>
            <Typography
              sx={{
                fontSize: { xs: '16px', md: '18px' },
                color: '#ffffff',
                mb: 3,
                lineHeight: 1.8
              }}
            >
              We believe the Sydney construction industry needs higher accountability, 
              stronger systems, and better customer care — and Clarence Homes exists to deliver exactly that.
            </Typography>
            
            <Box
              sx={{
                display: 'inline-block',
                backgroundColor: 'var(--primary-text-color)',
                px: 4,
                py: 2,
                borderRadius: 2,
                mt: 2
              }}
            >
              <Typography
                className="fw-bold"
                sx={{
                  fontSize: { xs: '18px', md: '24px' },
                  color: 'var(--primary-color)',
                  letterSpacing: 1
                }}
              >
                Raising Benchmarks Every Time
              </Typography>
            </Box>
            
            <Typography
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: 'var(--primary-text-color)',
                mt: 3,
                fontStyle: 'italic'
              }}
            >
              is not just a slogan — it is the standard we hold ourselves to on every project.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default OurMission;
